import React, { useState, useEffect, useCallback } from 'react';
import { fetchAllSheetData } from '../services/googleSheets';
import { detectPeriodEnd, todayISO } from '../utils/periods';

import { AppContext, useApp } from './appContextCore';


const MOCK_DOCUMENTS = [
  { id: 'd001', title: 'Safe Work Method Statement', category: 'WHS', status: 'current', version: '3.1', review_due: '2025-06-01', owner: 'Admin', file_type: 'pdf' },
  { id: 'd002', title: 'Subcontractor Agreement Template', category: 'Contracts', status: 'current', version: '2.0', review_due: '2025-12-01', owner: 'Admin', file_type: 'pdf' },
  { id: 'd003', title: 'Chain of Responsibility Policy', category: 'Transport', status: 'current', version: '1.5', review_due: '2025-09-01', owner: 'Admin', file_type: 'pdf' },
];

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState({ login: '', role: 'admin', team: null, name: '' });

  // Load current user role + team from cookie
  useEffect(() => {
    fetch('/.netlify/functions/current-user')
      .then(r => r.json())
      .then(d => { if (d.login) setCurrentUser(d); })
      .catch(() => {});
  }, []);

  const [xeroConnected, setXeroConnected] = useState(false);
  const [xeroOrg, setXeroOrg] = useState(null);
  const [xeroTokens, setXeroTokens] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [documents] = useState(MOCK_DOCUMENTS);
  const [sheetData, setSheetData] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(true);
  const [sheetError, setSheetError] = useState(null);
  const [odometerLog, setOdometerLog] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [rctis, setRctis] = useState(() => {
    try {
      const cached = localStorage.getItem('hp-rctis');
      if (!cached) return [];
      const parsed = JSON.parse(cached);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [rctiDrivers, setRctiDrivers] = useState([]);
  const [activityData, setActivityData] = useState([]);    // imported CSV rows
  const [activityMeta, setActivityMeta] = useState(null);  // { filename, dateRange, records, corrected }
  const [theme, setTheme] = useState(() => {
    // Light is the default. Any device still holding a cached 'dark' (engine or
    // portal — they share localStorage per origin) gets flipped to light ONCE,
    // keyed by a version flag. Bump the flag (…v3 → …v4) to re-flip everyone the
    // next time you need to reset the default. The toggle still sticks for
    // anyone who deliberately switches to dark afterwards.
    try {
      if (!localStorage.getItem('hp-theme-default-v4')) {
        localStorage.setItem('hp-theme-default-v4', '1');
        localStorage.setItem('hp-theme2', 'light');
        return 'light';
      }
      return localStorage.getItem('hp-theme2') || 'light';
    } catch { return 'light'; }
  });
  // Shared active pay period — seeded from Controls tab, kept in sync across pages
  const [activePeriodISO, setActivePeriodISO] = useState('');

  // Pending onboarding alerts — persisted in localStorage, auto-cleared when driver is found in Personnel
  const [pendingOnboarding, setPendingOnboarding] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hp-pending-onboarding') || '[]'); }
    catch { return []; }
  });

  // Morning van check — open issues (a driver flagged an Action, not yet handled).
  // Drives the red badge on the Service & Repairs nav item so a fault surfaces in
  // the engine, not just by SMS. Polled on mount, every 3 min, and on tab focus.
  const [inspectionAlertCount, setInspectionAlertCount] = useState(0);
  const refreshInspectionAlerts = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/vehicle-inspection-sb', { method: 'POST', body: JSON.stringify({ action: 'inspectionAlerts' }) });
      const data = await res.json();
      if (data && data.success) setInspectionAlertCount(data.count || 0);
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshInspectionAlerts();
    const iv = setInterval(refreshInspectionAlerts, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshInspectionAlerts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshInspectionAlerts]);

  // Leave requests waiting on a decision — drives the red badge on the Leave
  // Calendar nav item. That page is an iframe of roster-calendar.html, so the
  // count has to be read here; it deliberately mirrors the calendar's own queue
  // (new pending bookings PLUS approved bookings a driver has asked to amend or
  // cancel) so the badge number equals what you find when you open it.
  // Polled on mount, every 3 min, and on tab focus.
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  // The approved/pending entries themselves — the Dashboard's "on leave today" banner reads them.
  const [leaveEntries, setLeaveEntries] = useState([]);
  const refreshPendingLeave = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/roster-data');
      if (!res.ok) return;
      const data = await res.json();
      const entries = Array.isArray(data?.entries) ? data.entries : [];
      setLeaveEntries(entries);
      setPendingLeaveCount(entries.filter(e =>
        e.status === 'pending' || (e.status === 'approved' && e.change && e.change.kind)
      ).length);
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshPendingLeave();
    const iv = setInterval(refreshPendingLeave, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshPendingLeave(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshPendingLeave]);

  // Active mail stoppers still awaiting today's driver check-in (unassigned, or the
  // assigned driver hasn't answered at Onboard yet) — drives the red dot on the Mail
  // Stoppers nav. Resets each morning, drops as drivers confirm. Admin-gated endpoint;
  // non-admins 403 and the count stays 0.
  const [mailStopperAlertCount, setMailStopperAlertCount] = useState(0);
  const refreshMailStopperAlerts = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/mail-stopper-admin-sb', { method: 'POST', body: JSON.stringify({ action: 'alertCount' }) });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.success) setMailStopperAlertCount(data.count || 0);
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshMailStopperAlerts();
    const iv = setInterval(refreshMailStopperAlerts, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshMailStopperAlerts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshMailStopperAlerts]);

  // Night Sorters — a sorter has asked to be let off a shift ("can't work") and nobody has
  // answered yet. It badges Night Sorters because the request arrives in the portal with
  // nothing to announce it: unanswered, the night simply turns up short.
  const [sorterOfferAlertCount, setSorterOfferAlertCount] = useState(0);
  const [sorterOfferAlerts, setSorterOfferAlerts] = useState([]);
  const refreshSorterOfferAlerts = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getSorterAlertCount' }) });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.success) { setSorterOfferAlertCount(data.count || 0); setSorterOfferAlerts(data.offers || []); }
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshSorterOfferAlerts();
    const iv = setInterval(refreshSorterOfferAlerts, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshSorterOfferAlerts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshSorterOfferAlerts]);

  // Applicant "Request for Information" — badge on Personnel/Dashboard when a
  // candidate has returned their details and it hasn't been reviewed yet. Admin-
  // gated endpoint; non-admins 403 and the count stays 0.
  const [applicantInfoAlertCount, setApplicantInfoAlertCount] = useState(0);
  const [applicantInfoAlertNames, setApplicantInfoAlertNames] = useState([]);
  const refreshApplicantInfoAlerts = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/applicants-sb', { method: 'POST', body: JSON.stringify({ action: 'getInfoRequestAlerts' }) });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.success) {
        setApplicantInfoAlertCount(data.count || 0);
        setApplicantInfoAlertNames((data.items || []).map((i) => i.fullName).filter(Boolean));
      }
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshApplicantInfoAlerts();
    const iv = setInterval(refreshApplicantInfoAlerts, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshApplicantInfoAlerts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshApplicantInfoAlerts]);

  // Onboarding tasks waiting on whoever is signed in. A task somebody has to ACCEPT is
  // useless if nothing tells them it exists — this is what puts it on the nav from every
  // page. Admin-gated endpoint; non-admins 403 and the counts stay 0.
  const [onboardingTaskCount, setOnboardingTaskCount] = useState(0);      // red: not accepted yet
  const [onboardingInProgress, setOnboardingInProgress] = useState(0);    // amber: accepted, unfinished
  const [onboardingNames, setOnboardingNames] = useState([]);
  const refreshOnboardingTasks = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/onboarding-sb', { method: 'POST', body: JSON.stringify({ action: 'myTasks' }) });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.success) {
        setOnboardingTaskCount(data.unaccepted || 0);
        setOnboardingInProgress(data.inProgress || 0);
        setOnboardingNames(data.names || []);
      }
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshOnboardingTasks();
    const iv = setInterval(refreshOnboardingTasks, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshOnboardingTasks(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshOnboardingTasks]);

  // Open customer cases still awaiting triage (a driver not yet confirmed) — drives the red
  // dot on the Customer Cases nav. Admin-gated endpoint; non-admins 403 and the count stays 0.
  const [customerCaseAlertCount, setCustomerCaseAlertCount] = useState(0);
  // Amber signal: open cases where a driver has replied and the office hasn't answered yet,
  // plus cases resolved without the At fault / Not at fault call — those aren't finished
  // either, they just look it.
  const [customerCaseRespondedCount, setCustomerCaseRespondedCount] = useState(0);
  const [customerCaseFaultCount, setCustomerCaseFaultCount] = useState(0);
  const refreshCustomerCaseAlerts = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/customer-cases-admin-sb', { method: 'POST', body: JSON.stringify({ action: 'alertCount' }) });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.success) {
        setCustomerCaseAlertCount(data.count || 0);
        setCustomerCaseRespondedCount(data.responded || 0);
        setCustomerCaseFaultCount(data.awaitingFault || 0);
      }
    } catch { /* leave the last known count */ }
  }, []);
  useEffect(() => {
    refreshCustomerCaseAlerts();
    const iv = setInterval(refreshCustomerCaseAlerts, 3 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') refreshCustomerCaseAlerts(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [refreshCustomerCaseAlerts]);

  // Persist rctis to localStorage whenever they are built/updated
  useEffect(() => {
    if (rctis.length > 0) {
      try { localStorage.setItem('hp-rctis', JSON.stringify(rctis)); } catch {}
    }
  }, [rctis]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('hp-theme2', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  // Stable identity — otherwise any component whose data-load effect depends on
  // addNotification re-fetches on every app render (and a re-fetch landing mid-save
  // can clobber an optimistic update, e.g. a ticked-off to-do reappearing).
  const addNotification = useCallback((msg, type = 'info') => {
    const id = Date.now() + Math.random();
    setNotifications(n => [...n, { id, msg, type }]);
    setTimeout(() => setNotifications(n => n.filter(x => x.id !== id)), 4000);
  }, []);

  const buildRCTI = (loginName) => {
    if (!sheetData) return null;
    const { ratesMap, quantities, gccdSummary, adjustments, controls } = sheetData;

    const key = loginName.toUpperCase();
    const driverRates = ratesMap[loginName] || ratesMap[key] || {};
    const driverQty = quantities[key] || { Parcel: 0, Exp: 0, 'Depot Bags': 0, DOE: 0 };
    const gccd = gccdSummary[key] || {};
    const adj = adjustments[key] || {};

    const parcelAmt = (driverQty.Parcel || 0) * (driverRates['Parcel'] || 0);
    const expAmt = (driverQty.Exp || 0) * (driverRates['Exp'] || 0);
    const doeAmt = (driverQty.DOE || 0) * (driverRates['DOE'] || 0);
    const depotAmt = (driverQty['Depot Bags'] || 0) * (driverRates['Depot Bags'] || 0);
    const actualEarnings = parcelAmt + expAmt + doeAmt + depotAmt;

    const minPayable = gccd.minPayable || 0;
    const variance = actualEarnings - minPayable;
    const gccdLevy = variance < 0 ? Math.abs(variance) : 0;
    const operationalAdj = (adj.fuelLevy || 0) + (adj.tempFuel || 0) + (adj.adHoc || 0);
    const grossIncome = actualEarnings + gccdLevy + operationalAdj;

    const driver = personnel.find(p => p.loginName?.toUpperCase() === key);
    const leaseRate = driver?.leaseRate || 0;
    const leaseCap = driver?.leaseCap || 0;
    const fixedLeaseFee = driver?.leaseFee || 0;

    let leaseFee = 0;
    if (leaseRate > 0) {
      const rawLeaseFee = grossIncome * leaseRate;
      leaseFee = leaseCap > 0 ? Math.min(rawLeaseFee, leaseCap) : rawLeaseFee;
    } else if (fixedLeaseFee > 0) {
      leaseFee = fixedLeaseFee;
    }

    const subtotal = grossIncome - leaseFee;
    const gst = subtotal * 0.1;
    const total = subtotal + gst;

    return {
      loginName,
      invoiceNumber: `RCTI-${controls.nextInvoiceNumber || ''}`,
      reportEndDate: controls.reportEndDate || '',
      periodStart: controls.periodStart || null,
      periodEnd: controls.periodEnd || null,
      lineItems: [
        { description: 'Service Fee - Parcel Delivery Services - Route', quantity: driverQty.Parcel || 0, rate: driverRates['Parcel'] || 0, amount: parcelAmt },
        { description: 'Service Fee - 2nd Wave Express Post', quantity: driverQty.Exp || 0, rate: driverRates['Exp'] || 0, amount: expAmt },
        { description: 'Service Fee - Dept Of Education', quantity: driverQty.DOE || 0, rate: driverRates['DOE'] || 0, amount: doeAmt },
        { description: 'Service Fee - Depot Bags', quantity: driverQty['Depot Bags'] || 0, rate: driverRates['Depot Bags'] || 0, amount: depotAmt },
        ...(gccdLevy > 0 ? [{ description: 'Service Fee Adjustment - Route Conditions', quantity: 1, rate: gccdLevy, amount: gccdLevy }] : []),
        ...(operationalAdj > 0 ? [{ description: 'Service Fee Adjustment - Operational Conditions', quantity: 1, rate: operationalAdj, amount: operationalAdj }] : []),
      ],
      gccd: {
        totalHours: gccd.totalHours || 0,
        minRate: gccd.minRate || 0,
        minPayable,
        actualEarnings,
        variance,
        gccdLevy,
        meetsMinimum: variance >= 0,
      },
      deductions: {
        leaseFee,
        leaseRate,
        leaseCap,
        fixedLeaseFee,
        leaseLabel: driver?.leaseLabel || 'Van Lease Fee',
      },
      grossIncome,
      subtotal,
      gst,
      total,
    };
  };

  // Personnel loads once at app start, so anything that CREATES a driver must call this
  // afterwards — otherwise the new driver is in the database (and "Driver already
  // exists" on a second try) but missing from every list and search until a hard reload.
  const reloadPersonnel = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/drivers-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'listDriversFull' }) });
      const d = await res.json();
      if (d && d.success && Array.isArray(d.drivers)) setPersonnel(d.drivers);
    } catch (err) { console.warn('Personnel load (Supabase) failed:', err.message); }
  }, []);

  useEffect(() => {
    // Personnel comes from Supabase (drivers-sb) INDEPENDENTLY of the Google Sheets bulk,
    // so a Sheets outage can no longer blank the Personnel list or freeze the onboarding
    // badge (which only self-heals against a non-empty personnel list). The bulk stays the
    // fallback when the Supabase call itself fails.
    const loadPersonnel = reloadPersonnel;
    // Vehicles load from Supabase (fleet-sb) INDEPENDENTLY of the Sheets bulk, for the
    // same reason as personnel: the bulk /sheets call is slow (~15s) and when it throws
    // it used to take the nested fleet fetch down with it, leaving the fleet empty —
    // "0 commercial vehicles" and an unselectable Vehicle dropdown on Fleet › Vehicle Swaps.
    async function loadFleet() {
      try {
        const res = await fetch('/.netlify/functions/fleet-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getFleet' }) });
        const d = await res.json();
        if (d && d.success && Array.isArray(d.vehicles)) setVehicles(d.vehicles);
      } catch (err) { console.warn('Fleet load (Supabase) failed:', err.message); }
    }
    async function load() {
      setSheetLoading(true);
      setSheetError(null);
      loadPersonnel();
      loadFleet();
      try {
        const data = await fetchAllSheetData();
        setSheetData(data);
        // Fallback only — if the Supabase personnel load hasn't populated (e.g. it errored),
        // fill from the bulk so the page isn't empty.
        setPersonnel((prev) => (prev && prev.length ? prev : (data.drivers || [])));
        // Fallback only — if the Supabase fleet load hasn't populated (e.g. it errored),
        // fill from the bulk so the fleet isn't empty.
        setVehicles((prev) => (prev && prev.length ? prev : (data.vehicles || [])));
        setOdometerLog(data.odometerLog || []);
        // Seed active period from Controls if not already set by user
        if (data.controls?.reportEndDate) {
          const raw = data.controls.reportEndDate; // "DD-Mon-YYYY"
          const monthMap = {Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12'};
          const parts = raw.split('-');
          if (parts.length === 3) {
            const iso = `${parts[2]}-${monthMap[parts[1]] || '00'}-${parts[0].padStart(2,'0')}`;
            // The Controls anchor only fixes the 28-day grid; default to the cycle that
            // CONTAINS TODAY so the app auto-recognises the live period even if Controls
            // is a period or two behind.
            setActivePeriodISO(prev => prev || detectPeriodEnd(todayISO(), iso));
          }
        }
        addNotification('Sheet data loaded', 'success');
      } catch (err) {
        console.warn('Sheet load failed:', err.message);
        setSheetError(err.message);
        // A 403 just means this session isn't an admin/app login — i.e. every
        // driver-portal session, which doesn't use this admin bulk at all. Don't
        // show them a scary "check API key" banner. Real failures (network / 5xx)
        // still surface it for admins.
        if (err.status !== 403) {
          addNotification('Could not connect to Google Sheets - check API key', 'error');
        }
      } finally {
        setSheetLoading(false);
      }
    }
    load();
  }, []);

  // Auto-clear pending onboarding items once the driver appears in Personnel
  useEffect(() => {
    if (!personnel.length || !pendingOnboarding.length) return;
    const still = pendingOnboarding.filter(
      item => !personnel.some(p => (p.loginName || '').toUpperCase() === item.login.toUpperCase())
    );
    if (still.length !== pendingOnboarding.length) {
      setPendingOnboarding(still);
      try { localStorage.setItem('hp-pending-onboarding', JSON.stringify(still)); } catch {}
    }
  }, [personnel, pendingOnboarding]);

  const addPendingOnboarding = (logins) => {
    setPendingOnboarding(prev => {
      const existing = new Set(prev.map(p => p.login.toUpperCase()));
      const added = logins
        .filter(l => !existing.has(l.toUpperCase()))
        .map(login => ({ login, addedAt: new Date().toISOString() }));
      const next = [...prev, ...added];
      try { localStorage.setItem('hp-pending-onboarding', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const dismissPendingOnboarding = (login) => {
    setPendingOnboarding(prev => {
      const next = prev.filter(p => p.login.toUpperCase() !== login.toUpperCase());
      try { localStorage.setItem('hp-pending-onboarding', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  return (
    <AppContext.Provider value={{
      currentUser,
      xeroConnected, setXeroConnected,
      xeroOrg, setXeroOrg,
      xeroTokens, setXeroTokens,
      personnel, setPersonnel, reloadPersonnel,
      vehicles, setVehicles,
      odometerLog, setOdometerLog,
      documents,
      sheetData,
      setSheetData,
      sheetLoading,
      sheetError,
      buildRCTI,
      notifications,
      addNotification,
      theme,
      toggleTheme,
      rctis, setRctis,
      rctiDrivers, setRctiDrivers,
      activePeriodISO, setActivePeriodISO,
      activityData, setActivityData,
      activityMeta, setActivityMeta,
      pendingOnboarding, addPendingOnboarding, dismissPendingOnboarding,
      inspectionAlertCount, refreshInspectionAlerts,
      pendingLeaveCount, refreshPendingLeave, leaveEntries,
      mailStopperAlertCount, refreshMailStopperAlerts,
      customerCaseAlertCount, customerCaseRespondedCount, customerCaseFaultCount, refreshCustomerCaseAlerts,
      applicantInfoAlertCount, applicantInfoAlertNames, refreshApplicantInfoAlerts,
      onboardingTaskCount, onboardingInProgress, onboardingNames, refreshOnboardingTasks,
      sorterOfferAlertCount, sorterOfferAlerts, refreshSorterOfferAlerts,
    }}>
      {children}
      <Notifications items={notifications} />
    </AppContext.Provider>
  );
}

function Notifications({ items }) {
  if (!items.length) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 5000, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map(n => (
        <div key={n.id} style={{
          background: n.type === 'success' ? 'var(--hp-green)' : n.type === 'error' ? 'var(--hp-red)' : 'var(--hp-gold)',
          color: 'var(--hp-black)',
          padding: '10px 18px',
          borderRadius: 'var(--radius)',
          fontWeight: 600,
          fontSize: '0.875rem',
          boxShadow: 'var(--shadow)',
        }}>{n.msg}</div>
      ))}
    </div>
  );
}

export { useApp };
